//
//
//
//

import {
  AnimationState,
  AnimationStateData,
  ResizeMode,
  SceneRenderer,
  Shader,
  Skeleton,
  Vector2
} from '@esotericsoftware/spine-webgl';
import spineAssetService from '@/utils/spine-asset.service.js';

export default {
  name: 'SpineOptimized',
  props: {
    modelName: {
      type: String,
      required: true
    },
    modelNamePostfix: {
      type: String,
      default: ''
    },
    assetsPath: {
      type: String,
      required: true
    },
    animationName: {
      type: String,
      required: true
    },
    isJson: {
      type: Boolean,
      default: true
    },
    loop: {
      type: Boolean,
      default: false
    },
    staticAnimationName: {
      type: String,
      default: null
    },
    speed: {
      type: Number,
      default: 1
    },
    zoom: {
      type: Number,
      default: 1
    },
    fps: {
      type: Number,
      default: 35
    },
    largestAnimationName: {
      type: String,
      default: ''
    },
    offset: {
      type: Number,
      default: 0
    },
    premultipliedAlpha: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentSpeed: this.speed,
      currentOffset: this.offset
    };
  },
  watch: {
    animationName(newVal) {
      if (this.isInitialized) {
        this.changeAnimation(newVal);
      }
    },
    speed(newVal) {
      this.currentSpeed = newVal;
      if (this.isInitialized) {
        this.setSpeed(newVal);
      }
    },
    zoom() {
      if (this.isInitialized) {
        this.resize();
      }
    }
  },
  created() {
    this.trackIndex = 0;
    this.canvas = null;
    this.webglCtx = null;
    this.shader = null;
    this.skeleton = null;
    this.animationState = null;
    this.sceneRenderer = null;
    this.isPlaying = true;
    this.then = undefined;
    this.rafId = undefined;
    this.animationDuration = 0;
    this.cachedBounds = null;
    this.isInitialized = false;
    this.isLoading = false;
  },
  async mounted() {
    this.initCanvas();
    await this.initSpine();
  },
  beforeDestroy() {
    this.stopAnimation();
    this.cleanup();
  },
  methods: {
    /**
     * 初始化 Canvas 与 WebGL Context
     */
    initCanvas() {
      const container = this.$refs.container;
      if (!container) return;

      this.canvas = document.createElement('canvas');
      this.canvas.style.width = '100%';
      this.canvas.style.height = '100%';

      const w = container.offsetWidth;
      const h = container.offsetHeight;

      this.canvas.width = w;
      this.canvas.height = h;

      this.webglCtx = this.canvas.getContext('webgl', {
        alpha: true,
        premultipliedAlpha: this.premultipliedAlpha
      });

      if (!this.webglCtx) {
        const err = new Error('WebGL not supported');
        console.error(err);
        this.$emit('error', err);
        return;
      }

      container.appendChild(this.canvas);

      this.shader = Shader.newTwoColoredTextured(this.webglCtx);
      this.sceneRenderer = new SceneRenderer(this.canvas, this.webglCtx);
    },

    /**
     * 使用资源服务加载并生成专属 Skeleton 实例
     */
    async initSpine() {
      if (this.isLoading || !this.webglCtx) return;

      this.isLoading = true;

      try {
        const assetKey = this.generateAssetKey();

        const assetData = await spineAssetService.getSkeletonData(
          assetKey,
          this.modelName + (this.modelNamePostfix || ''),
          this.assetsPath,
          this.isJson,
          this.webglCtx
        );

        this.skeleton = new Skeleton(assetData.skeletonData);
        this.animationState = new AnimationState(
          new AnimationStateData(assetData.skeletonData)
        );

        this.setupSpineListeners();
        this.setAnimation(this.animationName, this.loop);

        this.resize();

        this.isInitialized = true;
        this.$emit('model-loaded');

        if (!this.staticAnimationName) {
          this.startAnimation();
        } else {
          this.renderStaticFrame();
        }
      } catch (error) {
        console.error('Failed to initialize Spine:', error);
        this.$emit('error', error);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 生成唯一缓存 Key
     */
    generateAssetKey() {
      const modelFullName = this.modelName + (this.modelNamePostfix || '');
      const format = this.isJson ? 'json' : 'skel';
      const normalizedPath = this.assetsPath.replace(/\/$/, '');

      return `${modelFullName}-${normalizedPath}-${format}`;
    },

    /**
     * 绑定 Spine 事件系统到 Vue Emit
     */
    setupSpineListeners() {
      if (!this.animationState) return;

      this.animationState.addListener({
        start: (trackEntry) => {
          if (trackEntry.trackIndex === this.trackIndex) {
            this.$emit('animation-start', this.animationName);
          }
        },
        complete: (trackEntry) => {
          if (trackEntry.trackIndex === this.trackIndex) {
            this.$emit('animation-complete', this.animationName);
          }
        },
        end: (trackEntry) => {
          if (trackEntry.trackIndex === this.trackIndex && this.loop) {
            this.$emit('animation-loop-complete', this.animationName);
          }
        }
      });
    },

    setAnimation(name, loop) {
      if (!this.animationState || !this.skeleton) return;

      try {
        this.animationState.setAnimation(this.trackIndex, name, loop);
        const current = this.animationState.getCurrent(this.trackIndex);
        this.animationDuration = current && current.animation ? current.animation.duration : 0;
      } catch (error) {
        console.error(`Failed to set animation "${name}":`, error);
      }
    },

    changeAnimation(name) {
      if (!this.isInitialized) return;

      this.setAnimation(name, this.loop);
      this.$emit('animation-change', name);
    },

    setSpeed(speed) {
      if (!this.animationState) return;

      const current = this.animationState.getCurrent(this.trackIndex);
      if (current) {
        current.timeScale = speed;
      }
    },

    startAnimation() {
      if (!this.isPlaying) {
        this.isPlaying = true;
      }
      this.animate(0);
    },

    stopAnimation() {
      this.isPlaying = false;

      if (this.rafId !== undefined) {
        window.cancelAnimationFrame(this.rafId);
        this.rafId = undefined;
      }
    },

    /**
     * 帧率截断动画主循环
     */
    animate(now) {
      if (!this.isPlaying || !this.skeleton || !this.animationState || !this.sceneRenderer) {
        return;
      }

      const fpsInterval = 1000 / this.fps;

      if (this.then === undefined) {
        this.then = now - this.currentOffset;
      }

      const elapsed = now - this.then;

      if (elapsed > fpsInterval) {
        this.then = now - (elapsed % fpsInterval);
        const delta = elapsed / 1000;

        this.animationState.update(delta);
        this.animationState.apply(this.skeleton);
        this.skeleton.updateWorldTransform();

        this.render();
      }

      this.rafId = window.requestAnimationFrame((timestamp) => this.animate(timestamp));
    },

    render() {
      if (!this.webglCtx || !this.sceneRenderer || !this.skeleton || !this.shader) {
        return;
      }

      this.webglCtx.clear(this.webglCtx.COLOR_BUFFER_BIT);
      this.sceneRenderer.begin();
      this.sceneRenderer.drawSkeleton(this.skeleton, this.premultipliedAlpha);
      this.sceneRenderer.end();
    },

    renderStaticFrame() {
      if (!this.staticAnimationName || !this.skeleton || !this.animationState) return;

      this.animationState.setAnimation(this.trackIndex, this.staticAnimationName, false);
      this.animationState.update(0);
      this.animationState.apply(this.skeleton);
      this.skeleton.updateWorldTransform();

      this.renderFrame();
    },

    renderFrame() {
      if (!this.skeleton || !this.animationState) return;

      this.animationState.apply(this.skeleton);
      this.skeleton.updateWorldTransform();
      this.render();
    },

    /**
     * 外部调整尺寸/相机矩阵方法
     */
    resize() {
      if (!this.canvas || !this.webglCtx || !this.sceneRenderer || !this.skeleton || !this.animationState) {
        return;
      }

      const container = this.$refs.container;
      if (!container) return;

      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;

      if (containerWidth === 0 || containerHeight === 0) return;

      this.canvas.width = containerWidth;
      this.canvas.height = containerHeight;

      this.webglCtx.viewport(0, 0, containerWidth, containerHeight);

      const currentTrack = this.animationState.getCurrent(this.trackIndex);

      this.sceneRenderer.resize(ResizeMode.Expand);
      const { camera } = this.sceneRenderer;

      if (currentTrack) {
        this.animationState.update(0);
        this.animationState.apply(this.skeleton);
      }

      this.skeleton.updateWorldTransform();

      let offset;
      let size;

      if (currentTrack) {
        offset = new Vector2();
        size = new Vector2();
        this.skeleton.getBounds(offset, size);
        this.cachedBounds = {
          offset: new Vector2(offset.x, offset.y),
          size: new Vector2(size.x, size.y)
        };
      } else if (this.cachedBounds) {
        offset = this.cachedBounds.offset;
        size = this.cachedBounds.size;
      } else {
        offset = new Vector2();
        size = new Vector2();
        this.skeleton.getBounds(offset, size);
      }

      const zoom = size.x > size.y ? size.x / this.canvas.width : size.y / this.canvas.height;

      camera.position.x = offset.x + size.x / 2;
      camera.position.y = offset.y + size.y / 2;
      camera.zoom = zoom / (this.zoom || 1);

      camera.update();
    },

    cleanup() {
      this.stopAnimation();

      this.skeleton = undefined;
      this.animationState = undefined;
      this.sceneRenderer = undefined;
      this.shader = undefined;

      if (this.webglCtx) {
        const loseContextExtension = this.webglCtx.getExtension('WEBGL_lose_context');
        if (loseContextExtension) {
          loseContextExtension.loseContext();
        }
        this.webglCtx = null;
      }

      if (this.canvas && this.canvas.parentNode) {
        this.canvas.parentNode.removeChild(this.canvas);
      }
    }
  }
};
